import { useState, useEffect } from "react";
import { Box, Stack } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { Row } from "./Row";
import { scrollToItem } from "../../../../../shared/utils/scroll-utils";
import { List as LazyList, RowComponentProps } from "react-window";
import { ConflictWarning } from "../ConflictWarning";
import { useStores } from "../../../../../shared/stores/RootStore";
import { rowHeight } from "./constants";
import { SpreadsheetHeader } from "./SpreadsheetHeader";

const headerHeightMultiplier = 3;

const SpreadsheetBase: React.FC = () => {
  const { codingStore, osaNodeStore, osaWayStore } = useStores();
  const [anchorRow, setAnchorRow] = useState<string>();
  const selectedOsaWay = osaWayStore.getSelected()!;
  const nodes = osaNodeStore.nodesByWayId.get(selectedOsaWay.id) ?? [];
  const selectedNode = osaNodeStore.getSelected();
  const nodeCodings = codingStore.getFromSelectedWay()!;

  useEffect(() => {
    if (selectedNode && nodes) {
      scrollToItem(`row-${selectedNode.id!}`);
    }
    // eslint-disable-next-line
  }, [!!selectedNode]);

  const setRowSelected = (
    nodeId: string,
    value: boolean,
    shiftKey: boolean,
  ) => {
    if (shiftKey && anchorRow) {
      const [firstIndex, lastIndex] = [
        nodes.findIndex((node) => node.id === anchorRow),
        nodes.findIndex((node) => node.id === nodeId),
      ].sort((a, b) => a - b);
      const nodeIdsToSelect = nodes
        .slice(firstIndex, lastIndex + 1)
        .map((node) => node.id!);

      osaNodeStore.setMultiSelectedIds(
        new Set([...osaNodeStore.multiSelectedIds, ...nodeIdsToSelect]),
      );
    } else {
      const newSet = new Set(osaNodeStore.multiSelectedIds);
      if (!value) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }

      osaNodeStore.setMultiSelectedIds(newSet);
      setAnchorRow(nodeId);
    }
  };

  function RowComponent({ index, style }: RowComponentProps<{}>) {
    if (index < headerHeightMultiplier) {
      return <Box style={style}></Box>;
    }
    const node = nodes[index - headerHeightMultiplier];
    const nodeCoding = nodeCodings.get(node.id)!;

    return (
      <Row
        key={node.id}
        style={style}
        index={index - headerHeightMultiplier}
        codingEntry={nodeCoding}
        setRowSelected={setRowSelected}
      />
    );
  }

  return (
    <Stack flexGrow={1} overflow="auto" position="relative" fontSize={"xs"}>
      <ConflictWarning />

      <LazyList
        rowComponent={RowComponent}
        rowCount={nodes.length + headerHeightMultiplier}
        rowHeight={rowHeight}
        rowProps={{}}
      >
        <SpreadsheetHeader />
      </LazyList>
    </Stack>
  );
};

export const Spreadsheet = observer(SpreadsheetBase);
